import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tab, Nav, Badge, Card, Row, Col, Dropdown } from "react-bootstrap";
import { FiArrowLeft, FiMoreVertical } from "react-icons/fi";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileArchive,
  FaFileAlt,
} from "react-icons/fa";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import CommonTable from "../components/CommonTable/CommonTable";
import EmptyState from "../components/EmptyState/EmptyState";
import { getSavedViewMode, saveViewMode } from "../../utils/viewPersistence";
import "../Documents/documentDrive.css";
import { 
  getVehicleById, 
  deleteVehicleDocument, 
  deleteVehicleInsurance, 
  deleteVehicleRegistration,
  getVehicleDocuments,
  getVehicleInsurances,
  getVehicleRegistrations
} from "../../services/VehicleService";
import axiosInstance from "../../services/AxiosInstance";
import { usePageToolbar } from "../layouts/nav/PageToolbarContext";

const VehicleDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [documents, setDocuments] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [viewMode, setViewMode] = useState(() => getSavedViewMode('vehicleDocuments', 'table'));

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    fetchVehicleData();
  }, [id]);

  const fetchVehicleData = async () => {
    try {
      setLoading(true);
      const res = await getVehicleById(id);
      setVehicle(res.data.data || res.data);
      await fetchRelatedData();
    } catch (error) {
      console.error("Vehicle fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load vehicle data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedData = async () => {
    try {
      const [docsRes, insRes, regRes] = await Promise.all([
        getVehicleDocuments(id).catch(() => ({ data: { data: [] } })),
        getVehicleInsurances(id).catch(() => ({ data: { data: [] } })),
        getVehicleRegistrations(id).catch(() => ({ data: { data: [] } }))
      ]);
      
      setDocuments(docsRes.data.data || []);
      setInsurances(insRes.data.data || []);
      setRegistrations(regRes.data.data || []);
    } catch (error) {
      console.error("Error fetching related data:", error);
    }
  };

  // Delete handlers
  const handleDocumentDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      await deleteVehicleDocument(id, documentId);
      notifySuccess("Document deleted successfully");
      fetchRelatedData();
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to delete document");
    }
  };

  // View mode handler
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    saveViewMode('vehicleDocuments', mode);
  };

  // File icon helper functions
  const getFileIcon = (file) => {
    if (!file || !file.file_type) return 'file';
    const fileType = file.file_type.toLowerCase();
    if (fileType.includes('pdf')) return 'pdf';
    if (fileType.includes('word') || fileType.includes('doc')) return 'word';
    if (fileType.includes('excel') || fileType.includes('sheet')) return 'excel';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'powerpoint';
    if (fileType.includes('image') || fileType.includes('jpeg') || fileType.includes('jpg') || fileType.includes('png') || fileType.includes('gif')) return 'image';
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('archive')) return 'archive';
    return 'file';
  };

  const getFileIconColor = (fileType) => {
    const colorMap = {
      pdf: '#d93025',
      word: '#2b579a',
      excel: '#1d6f42',
      powerpoint: '#d04423',
      image: '#34a853',
      archive: '#ea4335',
      file: '#667eea'
    };
    return colorMap[fileType] || '#667eea';
  };

  const getFileIconComponent = (file, size = 48) => {
    const iconType = getFileIcon(file);
    const color = getFileIconColor(iconType);
    
    switch (iconType) {
      case 'pdf':
        return <FaFilePdf size={size} style={{ color }} />;
      case 'word':
        return <FaFileWord size={size} style={{ color }} />;
      case 'excel':
        return <FaFileExcel size={size} style={{ color }} />;
      case 'powerpoint':
        return <FaFilePowerpoint size={size} style={{ color }} />;
      case 'image':
        return <FaFileImage size={size} style={{ color }} />;
      case 'archive':
        return <FaFileArchive size={size} style={{ color }} />;
      default:
        return <FaFileAlt size={size} style={{ color }} />;
    }
  };

  const handleInsuranceDelete = async (insuranceId) => {
    if (!window.confirm("Are you sure you want to delete this insurance record?")) return;
    try {
      await deleteVehicleInsurance(id, insuranceId);
      notifySuccess("Insurance deleted successfully");
      fetchRelatedData();
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to delete insurance");
    }
  };

  const handleRegistrationDelete = async (registrationId) => {
    if (!window.confirm("Are you sure you want to delete this registration record?")) return;
    try {
      await deleteVehicleRegistration(id, registrationId);
      notifySuccess("Registration deleted successfully");
      fetchRelatedData();
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to delete registration");
    }
  };

  const handleDocumentDownload = async (doc) => {
    if (!doc) {
      notifyError('Invalid document');
      return;
    }

    try {
      setLoading(true);
      
      // Always use vehicle document endpoint for vehicle documents
      const downloadUrl = `/vehicle/${id}/documents/${doc.id}/download`;

      const response = await axiosInstance.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/octet-stream'
        }
      });

      if (response.status === 200) {
        // Get filename from Content-Disposition header or use document title/file name
        let fileName = doc.document_title || doc.file_name || 'document';
        
        // Try to extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
          }
        }

        // Create blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        notifySuccess('File downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      
      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          notifyError(errorJson.message || 'Failed to download file');
        } catch (parseError) {
          notifyError('Failed to download file');
        }
      } else {
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to download file';
        notifyError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const vehicleDetailToolbar = useMemo(() => (
    <>
      <div className="page-toolbar-bar__start">
        <button
          type="button"
          className="page-toolbar-back-btn"
          onClick={() => navigate("/vehicle/vehicleList")}
          title="Back"
          aria-label="Back"
        >
          <FiArrowLeft />
          Back
        </button>
      </div>
      {vehicle ? (
        <div className="page-toolbar-bar__end">
          <button
            type="button"
            className="header-toolbar-btn seg-btn--wide"
            onClick={() => navigate(`/vehicle/editVehicle/${id}`)}
            title="Edit Vehicle"
            aria-label="Edit Vehicle"
          >
            <i className="fa fa-edit" aria-hidden />
            <span>Edit Vehicle</span>
          </button>
        </div>
      ) : null}
    </>
  ), [vehicle, id, navigate]);

  usePageToolbar(vehicleDetailToolbar, [vehicleDetailToolbar]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center p-4">
        <p>Vehicle not found</p>
        <button className="btn user-form-btn sw-btn-next" onClick={() => navigate("/vehicle/vehicleList")}>Back to List</button>
      </div>
    );
  }

  const staffName = vehicle.staff 
    ? `${vehicle.staff.employee_code || ''} - ${[vehicle.staff.first_name, vehicle.staff.middle_name, vehicle.staff.last_name].filter(Boolean).join(' ') || vehicle.staff.name || 'N/A'}`
    : 'N/A';

  return (
    <div>
      <ToastContainer />

      <Card className="mt-3">
        <Card.Header>
          <div
            className="d-flex flex-row justify-content-between align-items-center gap-2"
            style={{ width: '100%', paddingLeft: '15px', paddingRight: '15px' }}
          >
            <h4
              className="mb-0 text-truncate"
              style={{ fontSize: '1.1rem', wordBreak: 'break-word', maxWidth: '100%' }}
            >
              {vehicle.registration_number || 'Vehicle'} - {vehicle.vehicle_type || 'N/A'}
            </h4>
          </div>
        </Card.Header>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            {/* Mobile: Horizontal Scrollable Tabs */}
            <div className="d-md-none mb-3" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', msOverflowStyle: '-ms-autohiding-scrollbar' }}>
              <Nav as="ul" className="nav nav-pills" style={{ flexWrap: 'nowrap', minWidth: 'max-content', gap: '0.5rem' }}>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="details" 
                    className={activeTab === 'details' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Details
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="documents" 
                    className={activeTab === 'documents' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Documents
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="insurances" 
                    className={activeTab === 'insurances' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Insurance
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="registrations" 
                    className={activeTab === 'registrations' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Registration
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
            
            {/* Desktop: Tab Navigation */}
            <Nav as="ul" className="nav-tabs d-none d-md-flex">
              <Nav.Item as="li">
                <Nav.Link eventKey="details">Details</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="documents">Documents</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="insurances">Insurance</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="registrations">Registration</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content className="p-2 p-md-4">
              <Tab.Pane eventKey="details">
                {/* Mobile: single unified table for perfect alignment */}
                <Row className="d-md-none">
                  <Col xs={12}>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr><td><strong>Registration Number:</strong></td><td>{vehicle.registration_number || "N/A"}</td></tr>
                          <tr><td><strong>Vehicle Type:</strong></td><td>{vehicle.vehicle_type || "N/A"}</td></tr>
                          <tr><td><strong>Ownership Type:</strong></td><td>{vehicle.ownership_type || "N/A"}</td></tr>
                          <tr><td><strong>Maker:</strong></td><td>{vehicle.maker || "N/A"}</td></tr>
                          <tr><td><strong>Model:</strong></td><td>{vehicle.model || "N/A"}</td></tr>
                          <tr><td><strong>Color:</strong></td><td>{vehicle.color || "N/A"}</td></tr>
                          <tr><td><strong>Mileage:</strong></td><td>{vehicle.mileage || "N/A"}</td></tr>
                          <tr><td><strong>Usage Type:</strong></td><td>{vehicle.usage_type || "N/A"}</td></tr>
                          <tr><td><strong>Status:</strong></td><td>
                            <Badge bg={
                              vehicle.status === 'Active' ? 'success' : 
                              vehicle.status === 'Expired' ? 'danger' : 
                              vehicle.status === 'Deactivated' ? 'secondary' : 
                              'warning'
                            }>
                              {vehicle.status || 'Requested'}
                            </Badge>
                          </td></tr>
                          <tr><td><strong>Purchase Value:</strong></td><td>{vehicle.vehicle_purchase_value ? `₹${vehicle.vehicle_purchase_value.toLocaleString()}` : "N/A"}</td></tr>
                          <tr><td><strong>Assigned Staff:</strong></td><td>{staffName}</td></tr>
                          <tr><td><strong>Company:</strong></td><td>{vehicle.corporate_name || vehicle.Corporate?.name || "N/A"}</td></tr>
                          <tr><td><strong>Branch:</strong></td><td>{vehicle.branch_name || vehicle.Branch?.name || "N/A"}</td></tr>
                          <tr><td><strong>Department:</strong></td><td>{vehicle.department_name || vehicle.Department?.name || "N/A"}</td></tr>
                          <tr><td><strong>Sub Department:</strong></td><td>{vehicle.sub_department_name || vehicle.SubDepartment?.name || "N/A"}</td></tr>
                          <tr><td><strong>Created At:</strong></td><td>{vehicle.created_at ? new Date(vehicle.created_at).toLocaleString() : "N/A"}</td></tr>
                          <tr><td><strong>Updated At:</strong></td><td>{vehicle.updated_at ? new Date(vehicle.updated_at).toLocaleString() : "N/A"}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>

                {/* Desktop: two-column layout */}
                <Row className="d-none d-md-flex">
                  <Col md={6}>
                    <h5 className="d-none d-md-block">Vehicle Information</h5>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr><td><strong>Registration Number:</strong></td><td>{vehicle.registration_number || "N/A"}</td></tr>
                          <tr><td><strong>Vehicle Type:</strong></td><td>{vehicle.vehicle_type || "N/A"}</td></tr>
                          <tr><td><strong>Ownership Type:</strong></td><td>{vehicle.ownership_type || "N/A"}</td></tr>
                          <tr><td><strong>Maker:</strong></td><td>{vehicle.maker || "N/A"}</td></tr>
                          <tr><td><strong>Model:</strong></td><td>{vehicle.model || "N/A"}</td></tr>
                          <tr><td><strong>Color:</strong></td><td>{vehicle.color || "N/A"}</td></tr>
                          <tr><td><strong>Mileage:</strong></td><td>{vehicle.mileage || "N/A"}</td></tr>
                          <tr><td><strong>Usage Type:</strong></td><td>{vehicle.usage_type || "N/A"}</td></tr>
                          <tr><td><strong>Status:</strong></td><td>
                            <Badge bg={
                              vehicle.status === 'Active' ? 'success' : 
                              vehicle.status === 'Expired' ? 'danger' : 
                              vehicle.status === 'Deactivated' ? 'secondary' : 
                              'warning'
                            }>
                              {vehicle.status || 'Requested'}
                            </Badge>
                          </td></tr>
                          <tr><td><strong>Purchase Value:</strong></td><td>{vehicle.vehicle_purchase_value ? `₹${vehicle.vehicle_purchase_value.toLocaleString()}` : "N/A"}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                  <Col md={6}>
                    <h5 className="d-none d-md-block">Organizational Information</h5>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr><td><strong>Assigned Staff:</strong></td><td>{staffName}</td></tr>
                          <tr><td><strong>Company:</strong></td><td>{vehicle.corporate_name || vehicle.Corporate?.name || "N/A"}</td></tr>
                          <tr><td><strong>Branch:</strong></td><td>{vehicle.branch_name || vehicle.Branch?.name || "N/A"}</td></tr>
                          <tr><td><strong>Department:</strong></td><td>{vehicle.department_name || vehicle.Department?.name || "N/A"}</td></tr>
                          <tr><td><strong>Sub Department:</strong></td><td>{vehicle.sub_department_name || vehicle.SubDepartment?.name || "N/A"}</td></tr>
                          <tr><td><strong>Created At:</strong></td><td>{vehicle.created_at ? new Date(vehicle.created_at).toLocaleString() : "N/A"}</td></tr>
                          <tr><td><strong>Updated At:</strong></td><td>{vehicle.updated_at ? new Date(vehicle.updated_at).toLocaleString() : "N/A"}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
              </Tab.Pane>

              <Tab.Pane eventKey="documents">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                  <h5 className="mb-0 d-none d-md-block">Documents</h5>
                  <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap', width: '100%', justifyContent: 'flex-end' }}>
                    {/* View Toggle */}
                    <div className="view-toggle-container-modern staff-vehicle-view-toggle" style={{ flexShrink: 0, minWidth: '90px', width: 'auto' }}>
                      <button
                        className={`view-toggle-btn-modern ${viewMode === 'tile' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('tile')}
                        title="Card View"
                      >
                        <i className="fas fa-th-large"></i>
                      </button>
                      <button
                        className={`view-toggle-btn-modern ${viewMode === 'table' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('table')}
                        title="Table View"
                      >
                        <i className="fas fa-list"></i>
                      </button>
                    </div>
                  <button 
                    className="btn user-form-btn sw-btn-next"
                    onClick={() => navigate(`/vehicle/${id}/document/add`)}
                      style={{ 
                        height: '38px', 
                        padding: '0.375rem 0.75rem', 
                        fontSize: '0.875rem', 
                        flexShrink: 0,
                        lineHeight: '1.2',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto',
                        width: 'auto'
                      }}
                  >
                    Add Document
                  </button>
                  </div>
                </div>
                {viewMode === 'table' ? (
                <CommonTable
                  columns={[
                    {
                      key: 'document_title',
                      header: 'Title',
                      accessor: (row) => row.document_title || 'N/A'
                    },
                    {
                      key: 'issued_by',
                      header: 'Issued By',
                      accessor: (row) => row.issued_by || 'N/A'
                    },
                    {
                      key: 'issued_date',
                      header: 'Issued Date',
                      accessor: (row) => row.issued_date ? new Date(row.issued_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'expiry_date',
                      header: 'Expiry Date',
                      accessor: (row) => row.expiry_date ? new Date(row.expiry_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'verification_status',
                      header: 'Verification Status',
                      accessor: (row) => {
                        // Handle both string and object verification_status
                        let status = 'Pending';
                        if (typeof row.verification_status === 'string') {
                          status = row.verification_status;
                        } else if (row.verification_status && typeof row.verification_status === 'object') {
                          // If verification_status is an object, try to get the value property or name property
                          status = row.verification_status.value || row.verification_status.name || row.verification_status.verification_status || 'Pending';
                        } else if (row.verification_status) {
                          status = String(row.verification_status);
                        }
                        
                        const variant = status === 'Approved' ? 'success' : status === 'Rejected' ? 'danger' : 'warning';
                        return <Badge bg={variant}>{status}</Badge>;
                      }
                    }
                  ]}
                  data={documents}
                  actions={(row) => [
                    {
                      label: 'View',
                      icon: 'fas fa-eye',
                      onClick: () => navigate(`/vehicle/${id}/document/${row.id}`)
                    },
                    {
                      label: 'Download',
                      icon: 'fas fa-download',
                      onClick: () => handleDocumentDownload(row)
                    },
                    {
                      label: 'Edit',
                      icon: 'fas fa-edit',
                      onClick: () => navigate(`/vehicle/${id}/document/${row.id}/edit`)
                    },
                    {
                      label: 'Delete',
                      icon: 'fas fa-trash',
                      className: 'text-danger',
                      onClick: () => handleDocumentDelete(row.id)
                    }
                  ]}
                  emptyMessage="No documents found"
                  emptyStateIcon="fas fa-file-alt"
                  getRowId={(row) => row.id}
                  onRowClick={(row, rowIndex, e) => {
                    // Don't navigate if clicking on action button or action dropdown
                    if (e.target.closest('.table-actions') || e.target.closest('.dropdown-menu') || e.target.closest('.dropdown-toggle')) {
                      return;
                    }
                    navigate(`/vehicle/${id}/document/${row.id}`);
                  }}
                />
                ) : (
                  <Row className="drive-grid">
                    {documents.map((doc) => {
                      const fileIconType = getFileIcon(doc);
                      const iconColor = getFileIconColor(fileIconType);
                      const menuIcon = <FiMoreVertical size={18} />;
                      
                      return (
                        <Col key={doc.id} xs={6} sm={4} md={3} lg={2} xl={2} className="drive-item-col">
                          <Card 
                            className="drive-item file-item" 
                            onClick={(e) => {
                              const target = e.target;
                              if (target.closest('.drive-item-actions') || 
                                  target.closest('.dropdown') || 
                                  target.closest('.dropdown-toggle') ||
                                  target.closest('.dropdown-menu')) {
                                return;
                              }
                              navigate(`/vehicle/${id}/document/${doc.id}`);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="drive-item-icon" style={{ color: iconColor }}>
                              {getFileIconComponent(doc, 48)}
                            </div>
                            <div 
                              className="drive-item-name" 
                              title={doc.document_title || doc.name || doc.file_name}
                            >
                              {doc.is_favorite && (
                                <span className="me-1" style={{ color: '#ffc107' }}>★</span>
                              )}
                              {doc.document_title || doc.name || doc.file_name}
                            </div>
                            {doc.expiry_date && (
                              <div className="text-muted small mt-auto" style={{ fontSize: '0.75rem', marginTop: 'auto' }}>
                                Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                              </div>
                            )}
                            <div className="drive-item-actions" onClick={(e) => e.stopPropagation()}>
                              <Dropdown>
                                <Dropdown.Toggle 
                                  variant="link" 
                                  className="drive-menu-toggle-docs"
                                  id={`doc-dropdown-${doc.id}`}
                                >
                                  {menuIcon}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/vehicle/${id}/document/${doc.id}`);
                                    }}
                                  >
                                    <i className="fas fa-eye me-2"></i>
                                    View
                                  </Dropdown.Item>
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDocumentDownload(doc);
                                    }}
                                  >
                                    <i className="fas fa-download me-2"></i>
                                    Download
                                  </Dropdown.Item>
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/vehicle/${id}/document/${doc.id}/edit`);
                                    }}
                                  >
                                    <i className="fas fa-edit me-2"></i>
                                    Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDocumentDelete(doc.id);
                                    }}
                                    className="text-danger"
                                  >
                                    <i className="fas fa-trash me-2"></i>
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                    {documents.length === 0 && (
                      <Col xs={12}>
                        <EmptyState
                          icon={<FaFileAlt />}
                          title="No Data Found"
                          description="Get started by adding a document"
                        />
                      </Col>
                    )}
                  </Row>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="insurances">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                  <h5 className="mb-0 d-none d-md-block">Insurance Records</h5>
                  <button 
                    className="btn user-form-btn sw-btn-next"
                    onClick={() => navigate(`/vehicle/${id}/insurance/add`)}
                    style={{ maxWidth: '150px', width: 'auto', flexShrink: 0, padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    Add Insurance
                  </button>
                </div>
                <CommonTable
                  columns={[
                    {
                      key: 'insurance_provider_name',
                      header: 'Provider',
                      accessor: (row) => row.insurance_provider_name || 'N/A'
                    },
                    {
                      key: 'insurance_start_date',
                      header: 'Start Date',
                      accessor: (row) => row.insurance_start_date ? new Date(row.insurance_start_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'insurance_expiry_date',
                      header: 'Expiry Date',
                      accessor: (row) => row.insurance_expiry_date ? new Date(row.insurance_expiry_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'insurance_amount',
                      header: 'Amount',
                      accessor: (row) => row.insurance_amount ? `₹${row.insurance_amount.toLocaleString()}` : 'N/A'
                    },
                    {
                      key: 'is_current',
                      header: 'Current',
                      accessor: (row) => row.is_current ? <Badge bg="success">Current</Badge> : ''
                    }
                  ]}
                  data={insurances}
                  actions={(row) => [
                    {
                      label: 'Edit',
                      icon: 'fas fa-edit',
                      onClick: () => navigate(`/vehicle/${id}/insurance/${row.id}/edit`)
                    },
                    {
                      label: 'Delete',
                      icon: 'fas fa-trash',
                      className: 'text-danger',
                      onClick: () => handleInsuranceDelete(row.id)
                    }
                  ]}
                  emptyMessage="No insurance records found"
                  emptyStateIcon="fas fa-shield-alt"
                  getRowId={(row) => row.id}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="registrations">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                  <h5 className="mb-0 d-none d-md-block">Registration Records</h5>
                  <button 
                    className="btn user-form-btn sw-btn-next"
                    onClick={() => navigate(`/vehicle/${id}/registration/add`)}
                    style={{ maxWidth: '150px', width: 'auto', flexShrink: 0, padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    Add Registration
                  </button>
                </div>
                <CommonTable
                  columns={[
                    {
                      key: 'registered_number',
                      header: 'Registered Number',
                      accessor: (row) => row.registered_number || 'N/A'
                    },
                    {
                      key: 'registration_start_date',
                      header: 'Start Date',
                      accessor: (row) => row.registration_start_date ? new Date(row.registration_start_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'registration_end_date',
                      header: 'End Date',
                      accessor: (row) => row.registration_end_date ? new Date(row.registration_end_date).toLocaleDateString() : 'N/A'
                    }
                  ]}
                  data={registrations}
                  actions={(row) => [
                    {
                      label: 'Edit',
                      icon: 'fas fa-edit',
                      onClick: () => navigate(`/vehicle/${id}/registration/${row.id}/edit`)
                    },
                    {
                      label: 'Delete',
                      icon: 'fas fa-trash',
                      className: 'text-danger',
                      onClick: () => handleRegistrationDelete(row.id)
                    }
                  ]}
                  emptyMessage="No registration records found"
                  emptyStateIcon="fas fa-id-card"
                  getRowId={(row) => row.id}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      <InternalFooter />
    </div>
  );
};

export default VehicleDetails;
